import React, { useRef, useState } from 'react';
import { IioTable } from '../../components/IioTable';
import { useC3Chart } from '../../hooks/useC3Chart';
import { useProject } from '../../hooks/useProject';
import { hasPermission } from '../../types/project';
import { useBilling, useGenerateBillingReport } from '../../hooks/useBilling';
import { DownloadButton } from '../../components/DownloadButton';
import { useProjectStatus } from '../../hooks/useProjectStatus';
import { FormGroup } from '../../components/FormGroup';
import { AlertBlock } from '../../components/AlertBlock';
import { LoadingPlaceholder } from '../../components/LoadingPlaceholder';
import { CreditCardModal } from '../../components/modals/CreditCardModal';
import { useCurrentUserEmail } from '../../hooks/useCurrentUserEmail';
import { capitalize, formatBytes } from '../../utilities/format';
import {
  formatStatusDate,
  formatUtcDate,
  toIsoDateUtc,
  getFirstDayOfMonthUtc,
  getLastDayOfMonthUtc,
  shiftMonthUtc,
} from '../../utilities/date';

export function BillingTab({ projectId }: { projectId: string }) {
  const { data: project } = useProject(projectId);
  const [showCreditCard, setShowCreditCard] = useState(false);
  const [startDate, setStartDate] = useState(() => getFirstDayOfMonthUtc());
  const [endDate, setEndDate] = useState(() => getLastDayOfMonthUtc());

  const { data: billing, isLoading } = useBilling(projectId, startDate, endDate);
  const { data: statuses = [] } = useProjectStatus(projectId);
  const generateReport = useGenerateBillingReport(projectId);

  const chartRef = useRef<HTMLDivElement>(null);
  const myEmail = useCurrentUserEmail();

  const shiftMonth = (delta: number) => {
    const { startDate: newStart, endDate: newEnd } = shiftMonthUtc(startDate, delta);
    setStartDate(newStart);
    setEndDate(newEnd);
  };

  const stats = billing?.dailyStatistics ?? [];
  const hasChartData = billing != null && stats.some(
    s => (s.processingBytes ?? 0) > 0 || (s.rollingHostingBytes ?? 0) > 0
  );

  useC3Chart(chartRef, () => {
    if (!hasChartData) return null;
    const columns: [string, ...(string | number)[]][] = [
      ['x',          ...stats.map(s => toIsoDateUtc(s.date))],
      ['Processing', ...stats.map(s => s.processingBytes ?? 0)],
      ['Hosting',    ...stats.map(s => s.rollingHostingBytes ?? 0)],
    ];

    const allValues = [...columns[1].slice(1), ...columns[2].slice(1)] as number[];
    const max = Math.max(0, ...allValues);

    return {
      size: { height: 450 },
      data: { x: 'x', columns, type: 'bar' },
      bar: { width: { ratio: 0.3 } },
      color: { pattern: ['#0099CC', '#FF8800'] },
      axis: {
        x: {
          localtime: false,
          type: 'timeseries',
          tick: { format: '%Y-%m-%d', culling: { max: 50 }, rotate: 50, multiline: false },
        },
        y: {
          min: 0,
          max: Math.max(100_000_000, max),
          padding: { bottom: 0 },
          tick: { format: formatBytes },
        },
      },
      grid: { x: { show: false }, y: { show: false } },
      zoom: { enabled: false },
      subchart: { show: false },
    };
  }, [billing]);

  const canManageReports = project ? hasPermission(project, 'MANAGE_REPORTS') : false;
  const isAccountProject = project?.type === 'account_project';
  const hasCard = !!project?.card?.maskedNumber;
  const showForm = hasCard || !isAccountProject;

  return (
    <div>
      {canManageReports && (
        <DownloadButton
          title="Download CSV"
          testId="admin-billing-download-btn"
          disabled={generateReport.isPending}
          onClick={() => generateReport.mutate({ startDate, endDate })}
        />
      )}

      <h2><i className="fas fa-credit-card" aria-hidden="true" /> Project Billing</h2>

      {isAccountProject && (
        <button className="btn btn-primary" onClick={() => setShowCreditCard(true)} data-testid="admin-billing-card-btn">
          Update Credit Card <i className="fas fa-edit" aria-hidden="true" />
        </button>
      )}

      {isAccountProject && !hasCard && (
        <AlertBlock type="warning" heading="Warning!">
          No Credit Card on file. Please add a card to activate your project.{' '}
          <button className="btn btn-primary btn-sm" onClick={() => setShowCreditCard(true)} data-testid="admin-billing-add-card-btn">
            Add Credit Card
          </button>
        </AlertBlock>
      )}

      {showForm && (
        <form className="form-horizontal" noValidate>
          <h3>
            Billing Info{' '}
            <span className="label label-default" style={{ fontSize: 13 }}>As of Yesterday</span>
          </h3>

          <FormGroup label={<>Billing Period <span className="label label-default" style={{ fontSize: 11 }}>GMT</span></>} htmlFor="billing-period">
            <div style={{ display: 'table' }} data-testid="admin-billing-period-panel">
              <div style={{ display: 'table-cell', paddingRight: 15 }}>
                <button type="button" className="btn btn-sm btn-default" onClick={() => shiftMonth(-1)} data-testid="admin-billing-prev-btn">
                  Previous
                </button>
              </div>
              <div style={{ display: 'table-cell', verticalAlign: 'middle', paddingTop: 2 }}>
                {formatUtcDate(startDate)}
              </div>
              <div style={{ display: 'table-cell', verticalAlign: 'middle', padding: '2px 15px 0' }}>
                –
              </div>
              <div style={{ display: 'table-cell', verticalAlign: 'middle', paddingTop: 2 }}>
                {formatUtcDate(endDate)}
              </div>
              <div style={{ display: 'table-cell', paddingLeft: 15 }}>
                <button type="button" className="btn btn-sm btn-default" onClick={() => shiftMonth(1)} data-testid="admin-billing-next-btn">
                  Next
                </button>
              </div>
            </div>
          </FormGroup>

          {isLoading && <LoadingPlaceholder label="Loading billing data…" />}

          {!isLoading && !hasChartData && (
            <div style={{ height: 450, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, color: '#aaa', border: '1px dashed #ddd', borderRadius: 4 }}>
              <i className="fas fa-chart-bar" style={{ fontSize: 48 }} aria-hidden="true" />
              <p style={{ margin: 0, fontSize: 15 }}>No billing activity for this period.</p>
            </div>
          )}

          {hasChartData && <div ref={chartRef} />}

          {billing && (
            <>
              <FormGroup label={<span className="text-success">Total Project Size (GB)</span>} htmlFor="b-total">
                <p id="b-total" className="form-control-static">
                  {formatBytes(billing.totalProjectSize ?? 0)}{' '}
                  <span className="label label-default" style={{ fontSize: 12 }}>As of Today</span>
                </p>
              </FormGroup>
              <FormGroup label="Processing (GB)" htmlFor="b-processing">
                <p id="b-processing" className="form-control-static">{formatBytes(billing.processingBytes ?? 0)}</p>
              </FormGroup>
              <FormGroup label="Hosting (GB Days)" htmlFor="b-hosting">
                <p id="b-hosting" className="form-control-static">{formatBytes(billing.hostingByByteDays ?? 0)}</p>
              </FormGroup>
              <FormGroup label="Current Hosting (GB)" htmlFor="b-rolling">
                <p id="b-rolling" className="form-control-static">{formatBytes(billing.rollingHostingBytes ?? 0)}</p>
              </FormGroup>
              <FormGroup label="Native Exports (GB)" htmlFor="b-exports">
                <p id="b-exports" className="form-control-static">{formatBytes(billing.nativeExportsBytes ?? 0)}</p>
              </FormGroup>
              <FormGroup label="Image Exports Page Count" htmlFor="b-image">
                <p id="b-image" className="form-control-static">{(billing.imageExportsPageCount ?? 0).toLocaleString()}</p>
              </FormGroup>
              <FormGroup label="Translated Character Count" htmlFor="b-translated">
                <p id="b-translated" className="form-control-static">{(billing.translatedCharacterCount ?? 0).toLocaleString()}</p>
              </FormGroup>
              <FormGroup label="Transcription Seconds" htmlFor="b-transcription">
                <p id="b-transcription" className="form-control-static">{(billing.transcriptionSeconds ?? 0).toLocaleString()}</p>
              </FormGroup>
            </>
          )}

          {isAccountProject && hasCard && (
            <>
              <FormGroup label="Card Number" htmlFor="card-number">
                <input id="card-number" className="form-control" value={project!.card!.maskedNumber} readOnly />
              </FormGroup>
              <FormGroup label="Expires" htmlFor="card-expires">
                <input id="card-expires" className="form-control" value={project!.card!.expires ?? ''} readOnly />
              </FormGroup>
              <FormGroup label="Card Type" htmlFor="card-type">
                <input id="card-type" className="form-control" value={project!.card!.type ?? ''} readOnly />
              </FormGroup>
            </>
          )}
        </form>
      )}

      {statuses.length > 0 && (
        <>
          <h3>Status Log</h3>
          <IioTable data-testid="admin-billing-status-table">
            <thead>
              <tr>
                <th>Account</th>
                <th>Request Date</th>
                <th>Start Date</th>
                <th>From</th>
                <th>To</th>
              </tr>
            </thead>
            <tbody>
              {statuses.map((s, i) => (
                <tr key={i} className={s.toStatus === 'hibernate' ? 'warning' : s.toStatus === 'purged' ? 'danger' : ''} data-testid="admin-billing-status-row">
                  <td>
                    {s.email === myEmail && <i className="fas fa-user" title="Me" aria-label="Me" />}{' '}
                    {s.fullName}{' '}
                    <small className="text-muted">{s.email}</small>
                  </td>
                  <td>{formatStatusDate(s.requestDate)}</td>
                  <td>{formatStatusDate(s.startDate)}</td>
                  <td>{capitalize(s.fromStatus)}</td>
                  <td>{capitalize(s.toStatus)}</td>
                </tr>
              ))}
            </tbody>
          </IioTable>
        </>
      )}
    {showCreditCard && (
        <CreditCardModal projectId={projectId} onClose={() => setShowCreditCard(false)} />
      )}
    </div>
  );
}
